import {
  NEW_USED_LIST,
  NEW_YEAR_FUTURE,
  USED_YEAR_PAST
} from '../../utils/constants';

/**
 * Return true if the the user has selected the new option
 */
export function isNew(state) {
  return state.selected.newUsed === NEW_USED_LIST.new.value;
}

/**
 * Returns true if a given variant passes the selected state criteria
 *
 * @param variant The variant to test
 * @param state The state with selected criteria
 * @param ignoreSelectedYear If true, ignore the selected year check
 */
function variantPassesStateSelections(variant, state, ignoreSelectedYear) {
  const variantTransmissions =
    variant.transmissionId && variant.transmissionId.toLowerCase();
  const selectedTransmissions =
    state.selected.transmission && state.selected.transmission.toLowerCase();
  const variantBodyType =
    variant.bodyTypeId && variant.bodyTypeId.toLowerCase();
  const selectedBodyType =
    state.selected.bodyType && state.selected.bodyType.toLowerCase();
  return (
    (ignoreSelectedYear
      ? true
      : parseInt(variant.modelYear) === parseInt(state.selected.year)) &&
    variantTransmissions === selectedTransmissions &&
    variantBodyType === selectedBodyType
  );
}

/**
 * Return the new used list in a common format for binding to the UI
 */
export function getNewUsedList() {
  return Object.values(NEW_USED_LIST).map((item) => ({
    key: item.value,
    text: item.label
  }));
}

/**
 * Get a list of body types based on the selected make
 */
export function getBodyList(state) {
  const makeList = state.makeList || [];
  const matchId = state.selected.makeId;
  const selectedMake = makeList.find((make) => make.id === matchId);
  return selectedMake ? selectedMake.bodyTypes : [];
}

/**
 * Return the full selected model
 */
function getSelectedModel(state = {}) {
  const modelList = state.modelList || [];
  return modelList.find((model) => model.id === state.selected.modelId);
}

/**
 * Return a list of transmissions for the selected model. Exclude transmissions
 * that do not have any variants with a matching transmission.
 */
export function getTransmissionList(state) {
  const selectedModel = getSelectedModel(state);
  if (!selectedModel) {
    return [];
  }
  const variantTransmissions = {};
  selectedModel.variants.forEach((variant) => {
    variantTransmissions[variant.transmissionId] = true;
  });
  return selectedModel.transmissionGroups.filter(
    (transmissionGroup) => variantTransmissions[transmissionGroup.id]
  );
}

/**
 * Return a list of years from the current year (based on new / used selection)
 * that have at least one variant from the models list that matches the year
 */
export function getYearList(state, getters) {
  const variantYears = {};

  const selectedModel = getSelectedModel(state);
  if (!selectedModel) {
    return [];
  }

  selectedModel.variants.forEach((variant) => {
    if (variantPassesStateSelections(variant, state, true)) {
      variantYears[variant.modelYear] = true;
    }
  });

  let yearList = [];
  const currentYear = new Date().getFullYear();
  const offset = getters.isNew ? NEW_YEAR_FUTURE : USED_YEAR_PAST;
  const startingYear = currentYear - 1;

  for (let yearCount = 0; yearCount <= offset; yearCount++) {
    let year = getters.isNew
      ? startingYear + yearCount
      : startingYear - yearCount;
    if (variantYears[year]) {
      yearList.push(year);
    }
  }
  return yearList;
}

/**
 * Get a list of variants that match the car search selections and add some fields
 * that will be used in the UI
 */
export function getVariants(state) {
  let variantList = [];
  const selectedModel = getSelectedModel(state);
  if (!selectedModel) {
    return [];
  }
  selectedModel.variants.forEach((variant) => {
    if (variantPassesStateSelections(variant, state)) {
      variantList.push({
        ...variant,
        title: `${variant.modelYear} ${selectedModel.makeName} ${selectedModel.name}`,
        model: selectedModel.name
      });
    }
  });

  return variantList;
}

/**
 * Determine if the car search page has all inputs with a selection
 */
export function validCarSearch(state) {
  for (const key in state.selected) {
    if (!state.selected[key]) {
      return false;
    }
  }
  return true;
}

export function selected(state, getters, rootState, rootGetters) {
  const isExistingQuoteOpen = rootGetters['quote/isExistingQuoteOpen'];

  if (isExistingQuoteOpen) {
    const { variant, make, bodyType, model } = rootState.quote.quote;

    return {
      newUsed:
        parseInt(variant.modelYear) >= new Date().getFullYear()
          ? NEW_USED_LIST.new.value
          : NEW_USED_LIST.used.value,
      makeId: make.id,
      bodyType: bodyType,
      modelId: model.id,
      transmission: variant.transmission,
      year: variant.modelYear
    };
  }

  return state.selected;
}
